import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import React from 'react'

interface BaseProps {
  absolute?: boolean
  component?: DividerComponent
}

type OrientationProps =
  | {
      orientation?: 'horizontal'
      flex?: never
    }
  | {
      orientation: 'vertical'
      flex?: boolean
    }

type VariantProps =
  | {
      insetSize?: DividerInsetSize
      variant: 'inset'
    }
  | {
      insetSize?: never
      variant?: DividerVariant
    }

export type DividerProps = BaseProps & VariantProps & OrientationProps

type DividerComponent = 'hr' | 'li'

type DividerInsetSize = 'small' | 'medium' | 'large'

type DividerVariant = 'fullWidth' | 'inset' | 'middle'

const variants = {
  fullWidth: '',
  inset: {
    small: 'ml-4',
    medium: 'ml-15',
    large: 'ml-18',
  },
  middle: 'mx-4',
}

function Divider({
  absolute = false,
  component: Component = 'hr',
  flex = false,
  insetSize = 'small',
  orientation = 'horizontal',
  variant = 'fullWidth',
}: PropsWithChildren<DividerProps>): JSX.Element {
  const width = orientation === 'vertical' ? 'w-px' : absolute ? 'w-full' : null
  const height =
    orientation === 'vertical'
      ? flex && !absolute
        ? 'self-stretch h-auto'
        : 'h-full'
      : 'h-px'

  return (
    <Component
      aria-hidden="true"
      className={clsx(
        'flex-shrink-0 m-0 border-none bg-black-10',
        absolute ? 'absolute bottom-0 left-0' : null,
        variant === 'inset' ? variants.inset[insetSize] : variants[variant],
        width,
        height,
      )}
      role="separator"
    />
  )
}

export default Divider
